/* TALIAZ EXECUTIVE CONTROL — LIGHT EDITION
   ----------------------------------------
   One palette for the executive screens, defined once so a colour is never typed
   twice. Every value here comes from the approved design direction (18/10/2026);
   nothing is invented and nothing is "close enough".

   🔴 LIME IS NOT AN INTERFACE COLOUR. It marks the brand, and only twice on a
   screen: the crown beside the main heading, and the single handwritten note on a
   genuinely exceptional measured number. A lime button or a lime chart series is a
   bug, not a choice. */

import type { CSSProperties } from 'react';

export const T = {
  // ── surfaces ──────────────────────────────────────────────────────────────
  bg: '#F7F9FC',
  card: '#FFFFFF',
  sidebar: '#101D35',
  // One step up from the sidebar, for rows that must separate from it without a
  // border: hover states, the signed-in footer, the active pill's ground.
  sidebarRaised: '#1A2947',
  subtle: '#F1F5F9',

  // ── text ──────────────────────────────────────────────────────────────────
  // 🔴 נמדד 22/10 מול לבן: #94A3B8 נותן 2.56:1 ו-#64748B נותן 4.76:1, כששני
  // הסף הוא 4.5. על רקע כהה אפור בהיר הוא הבחירה הנכונה — על לבן הוא נעלם,
  // וזה מה שהטשטש את כל תוויות הגרפים אחרי המעבר לבהיר. הסקאלה ירדה דרגה:
  //   ink 16.81 · inkSoft 7.58 · inkFaint 4.76 — שלושתן עוברות.
  ink: '#101D35',
  inkSoft: '#475569',
  inkFaint: '#64748B',
  onDark: '#E2E8F0',
  onDarkSoft: '#8396B5',

  // ── semantic ──────────────────────────────────────────────────────────────
  blue: '#2563EB',
  green: '#10B981',
  red: '#EF4444',
  orange: '#F59E0B',
  lime: '#B8F500',

  // Text ON a tint. Amber at #F59E0B is unreadable on #FFFBEB, so a warning
  // sentence inside an amber panel uses this and not the signal colour itself.
  orangeInk: '#92400E',
  //: ואותו דבר לירוק. #10B981 נותן 2.54:1 על לבן — נכון לבר, בלתי קריא כטקסט.
  greenInk: '#047857',

  // Tints. Used for chart fills and alert grounds, never for text.
  blueTint: '#EFF4FE',
  greenTint: '#ECFDF5',
  redTint: '#FEF2F2',
  orangeTint: '#FFFBEB',

  // ── structure ─────────────────────────────────────────────────────────────
  border: '#E2E8F0',
  borderSoft: '#F1F5F9',
  radius: 12,
  radiusSm: 8,
  shadow: '0 1px 2px rgba(16,24,40,.04), 0 1px 3px rgba(16,24,40,.03)',
  shadowRaised: '0 4px 12px rgba(16,24,40,.06), 0 1px 3px rgba(16,24,40,.04)',
} as const;

/* Chart series. A FIXED order, never cycled by index at render time: Maccabi keeps
   its blue when a filter removes Clalit, so the eye does not re-learn the chart
   every time the data changes. Five hues, all legible against white, all
   distinguishable in the common forms of colour blindness — and every series is
   direct-labelled anyway, so identity never rests on hue alone. */
export const SERIES = ['#2563EB', '#10B981', '#8B5CF6', '#F59E0B', '#64748B'] as const;

/* Margin grading, one rule for the whole screen. Written once because it was
   previously re-typed in six tables with three different thresholds. */
export const marginColor = (pct: number): string =>
  pct >= 50 ? T.green : pct >= 25 ? T.orange : T.red;

/* ── formatters ───────────────────────────────────────────────────────────────
   Two decimals on every fractional number (Ohad's standing rule); whole counts stay
   whole. `ils0` is the rounded form for tiles, where the agorot are noise at 21px. */
/* 🔴 A NEGATIVE amount is written in parentheses, never with a leading minus.
   "₪-327.36" inside an RTL paragraph is reordered by the bidi algorithm into
   "-327.36₪" — the sign jumps the number and the currency lands on the far side.
   Accounting parentheses carry the same meaning, are the convention a CFO already
   reads, and contain no character whose direction can be re-decided.
   ⚠ And this is deliberately NOT solved with U+2066/U+200F: hidden directional
   marks come out as literal `\u200F` in Ohad's terminal and in copied text. */
const shekel = (body: string, negative: boolean) =>
  negative ? `(₪${body})` : `₪${body}`;

export const ils = (n: number) =>
  shekel(Math.abs(n).toLocaleString('he-IL', { minimumFractionDigits: 2, maximumFractionDigits: 2 }), n < 0);
export const ils0 = (n: number) =>
  shekel(Math.abs(Math.round(n)).toLocaleString('he-IL'), n < 0);
export const num = (n: number) => n.toLocaleString('he-IL');

/* Every fractional number carries two decimals — Ohad's standing rule. Percentages
   are fractional, so they go through here and not through a local toFixed(0).
   The screen used to print the same share as "77%" in a donut arc and "77.0%" in
   the legend 200px away, and the same survival rate as "7%" and "7.40%" inside one
   card, which makes a reader doubt the rounding everywhere else. */
export const pct2 = (n: number) => `${n.toFixed(2)}%`;

/* ── shared style objects ─────────────────────────────────────────────────────
   Inline styles rather than Tailwind classes, because these screens draw SVG and
   computed colours that Tailwind's static scan cannot see. Keeping them here means
   a card looks the same in eleven panels without eleven copies of the recipe. */
export const card: CSSProperties = {
  background: T.card,
  border: `1px solid ${T.border}`,
  borderRadius: T.radius,
  boxShadow: T.shadow,
};

export const cardPad: CSSProperties = { ...card, padding: '16px 18px' };

/* Numbers in a column must line up. `tabular-nums` makes every digit the same
   width, so ₪1,183,498.00 and ₪425,309.00 share a decimal point. */
export const tnum: CSSProperties = { fontVariantNumeric: 'tabular-nums' };

export const h3: CSSProperties = {
  fontSize: 14, fontWeight: 700, color: T.ink, letterSpacing: '-.01em',
  marginBottom: 14, display: 'flex', alignItems: 'baseline', gap: 6, flexWrap: 'wrap',
};

/* The grey continuation of a panel title — "· ימים שחלפו בחודש בלבד". Same line,
   lower voice: it qualifies the title rather than competing with it. */
export const h3Note: CSSProperties = {
  fontSize: 11.5, fontWeight: 400, color: T.inkSoft,
};

export const th: CSSProperties = {
  fontSize: 11, fontWeight: 700, color: T.inkSoft, padding: '9px 16px',
  textTransform: 'none', whiteSpace: 'nowrap', background: T.bg,
  borderBottom: `1px solid ${T.border}`,
};

export const td: CSSProperties = {
  fontSize: 13, padding: '10px 16px', color: T.ink,
  borderTop: `1px solid ${T.borderSoft}`,
};
